/* NephrologyPanel — безплатни нефрологични калкулатори
 *
 * 4 модула в един CollapsiblePanel:
 *   1. eGFR (CKD-EPI 2021) + CKD stage
 *   2. Kidney stone risk (Robertson 1988)
 *   3. Daily PRAL от meal log
 *
 * Auto-fill от последните лабораторни резултати (creatinine → US units).
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { toast } from "sonner";
import CollapsiblePanel from "./CollapsiblePanel";
import CkdStagesTable from "./CkdStagesTable";
import { ComplianceDisclaimer } from "@/components/compliance/ComplianceDisclaimer";
import { Activity, Zap, Droplet, Scale, Layers } from "lucide-react";

// Mini-tab registry so we can switch between the 4 calculators inside the panel.
const TABS = [
  { id: "egfr",        label: "eGFR + CKD",    icon: Activity },
  { id: "stages",      label: "CKD 5 стадия",  icon: Layers },
  { id: "stone",       label: "Бъбречни камъни", icon: Scale },
  { id: "pral",        label: "Дневна PRAL",    icon: Droplet },
];

// ─── eGFR / CKD stage mini-form ──────────────────────────────────────────
function EgfrForm({ API }) {
  const [creatUnit, setCreatUnit] = useState("mg/dL");
  const [creat, setCreat] = useState("");
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("male");
  const [acr, setAcr] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autofilling, setAutofilling] = useState(false);

  const convertCreat = useCallback((value) => {
    const v = Number(value);
    if (Number.isNaN(v)) return NaN;
    return creatUnit === "µmol/L" ? v / 88.4017 : v;
  }, [creatUnit]);

  const submit = async () => {
    const mgdl = convertCreat(creat);
    if (Number.isNaN(mgdl) || mgdl <= 0 || !age) {
      toast.error("Попълнете креатинин и възраст");
      return;
    }
    setLoading(true);
    try {
      const payload = { creatinine_mg_dl: Number(mgdl.toFixed(3)), age: Number(age), sex };
      const endpoint = acr ? "/nephrology/ckd-stage" : "/nephrology/egfr";
      const body = acr ? { ...payload, acr_mg_g: Number(acr) } : payload;
      const { data } = await axios.post(`${API}${endpoint}`, body);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при изчисление");
    } finally {
      setLoading(false);
    }
  };

  const autofill = async () => {
    setAutofilling(true);
    try {
      const { data } = await axios.get(`${API}/nephrology/from-latest-lab`);
      if (data.creatinine_mg_dl != null) {
        setCreatUnit("mg/dL");
        setCreat(String(data.creatinine_mg_dl));
      }
      if (data.age) setAge(String(data.age));
      if (data.sex) {
        const s = String(data.sex).toLowerCase();
        setSex(s.startsWith("f") || s.includes("жен") ? "female" : "male");
      }
      if (data.acr_mg_g != null) setAcr(String(data.acr_mg_g));
      toast.success(
        data.ready_to_calculate
          ? "Попълнено от последната лаборатория!"
          : "Частично попълване — проверете празните полета."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Няма намерени лабораторни резултати");
    } finally {
      setAutofilling(false);
    }
  };

  // Unified view for both endpoints: /egfr returns the stage directly,
  // /ckd-stage wraps it under `result.egfr.*`.
  const eg = result?.egfr?.egfr !== undefined ? result.egfr : result;
  const overall = result?.overall_risk;

  return (
    <div className="space-y-3" data-testid="nephrology-egfr-form">
      <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 flex items-start gap-3">
        <div className="shrink-0 rounded-md bg-violet-100 p-1.5">
          <Zap size={16} className="text-violet-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-violet-900">Попълни автоматично</div>
          <div className="text-[11px] text-violet-800 mt-0.5">
            Извличаме креатинин, възраст и пол от последната лаборатория.
          </div>
        </div>
        <button
          onClick={autofill}
          disabled={autofilling}
          className={`shrink-0 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
            autofilling ? "bg-gray-300 text-gray-500" : "bg-violet-600 text-white hover:bg-violet-700"
          }`}
          data-testid="nephrology-autofill-btn"
        >
          {autofilling ? "Извличане…" : "Попълни"}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="flex items-center gap-1 text-[11px] font-medium text-gray-700 mb-0.5">
            <span>Креатинин</span>
            <select
              value={creatUnit}
              onChange={(e) => setCreatUnit(e.target.value)}
              className="ml-1 border border-gray-300 rounded px-1 py-0 text-[10px] bg-white text-gray-600"
              data-testid="nephrology-creat-unit"
            >
              <option value="mg/dL">mg/dL</option>
              <option value="µmol/L">µmol/L</option>
            </select>
          </span>
          <input
            type="number"
            step="0.01"
            value={creat}
            onChange={(e) => setCreat(e.target.value)}
            placeholder={creatUnit === "mg/dL" ? "0.6–1.3" : "53–115"}
            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
            data-testid="nephrology-creat-input"
          />
        </label>

        <label className="block">
          <span className="text-[11px] font-medium text-gray-700 mb-0.5 block">Възраст</span>
          <input
            type="number"
            step="1"
            min={18}
            max={110}
            value={age}
            onChange={(e) => setAge(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
            data-testid="nephrology-age-input"
          />
        </label>

        <label className="block">
          <span className="text-[11px] font-medium text-gray-700 mb-0.5 block">Пол</span>
          <select
            value={sex}
            onChange={(e) => setSex(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm bg-white focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
            data-testid="nephrology-sex-select"
          >
            <option value="male">Мъж</option>
            <option value="female">Жена</option>
          </select>
        </label>

        <label className="block">
          <span className="text-[11px] font-medium text-gray-700 mb-0.5 block">
            ACR <span className="text-gray-400 font-normal">(по избор · mg/g)</span>
          </span>
          <input
            type="number"
            step="0.1"
            value={acr}
            onChange={(e) => setAcr(e.target.value)}
            placeholder="< 30 нормално"
            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
            data-testid="nephrology-acr-input"
          />
        </label>
      </div>

      <button
        onClick={submit}
        disabled={loading}
        className={`w-full rounded-md px-4 py-2 text-sm font-medium text-white ${
          loading ? "bg-gray-300 cursor-not-allowed" : "bg-violet-600 hover:bg-violet-700"
        }`}
        data-testid="nephrology-egfr-submit"
      >
        {loading ? "Изчисляване…" : "Изчисли eGFR (безплатно)"}
      </button>

      {eg && (
        <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3" data-testid="nephrology-egfr-result">
          <div className="flex items-baseline justify-between">
            <span className="text-[11px] uppercase tracking-wide text-gray-500">eGFR</span>
            <span className="text-2xl font-bold" style={{ color: eg.color }}>
              {eg.egfr} <span className="text-sm font-normal text-gray-500">{eg.unit}</span>
            </span>
          </div>
          <div
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium"
            style={{ background: `${eg.color}20`, color: eg.color }}
          >
            {eg.stage_bg}
          </div>
          {result?.acr && (
            <div className="text-xs text-gray-600">
              ACR: <strong>{result.acr.acr} mg/g</strong> — {result.acr.stage_bg}
            </div>
          )}
          {overall && (
            <div className="mt-2 rounded-md bg-gray-50 px-3 py-2 text-xs">
              <span className="font-semibold text-gray-700">KDIGO комбиниран риск:</span>{" "}
              <span style={{ color: result.overall_color }} className="font-semibold">{overall}</span>
            </div>
          )}
          <div className="text-[10px] text-gray-400 italic leading-relaxed pt-1 border-t border-gray-100">
            {eg.reference}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Kidney stone risk form ──────────────────────────────────────────────
function KidneyStoneForm({ API }) {
  const [water, setWater] = useState(2000);
  const [sodium, setSodium] = useState(2300);
  const [calcium, setCalcium] = useState(1000);
  const [oxalate, setOxalate] = useState(250);
  const [protein, setProtein] = useState(70);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/nephrology/kidney-stone-risk`, {
        daily_water_ml: Number(water),
        daily_sodium_mg: Number(sodium),
        daily_calcium_mg: Number(calcium),
        daily_oxalate_mg: Number(oxalate),
        daily_animal_protein_g: Number(protein),
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при изчисление");
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { label: "Вода (ml/ден)",   value: water,   set: setWater,   step: 50, tid: "stone-water" },
    { label: "Натрий (mg/ден)", value: sodium,  set: setSodium,  step: 50, tid: "stone-sodium" },
    { label: "Калций (mg/ден)", value: calcium, set: setCalcium, step: 50, tid: "stone-calcium" },
    { label: "Оксалат (mg/ден)", value: oxalate, set: setOxalate, step: 10, tid: "stone-oxalate" },
    { label: "Жив. протеин (g)", value: protein, set: setProtein, step: 5, tid: "stone-protein" },
  ];

  return (
    <div className="space-y-3" data-testid="nephrology-stone-form">
      <p className="text-xs text-gray-600 leading-snug">
        Калкулаторът оценява риск от калций-оксалатни камъни според дневен прием на вода, натрий,
        калций, оксалат и животински протеин.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {fields.map((f) => (
          <label key={f.tid} className="block">
            <span className="text-[11px] font-medium text-gray-700 mb-0.5 block">{f.label}</span>
            <input
              type="number"
              step={f.step}
              value={f.value}
              onChange={(e) => f.set(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
              data-testid={`nephrology-${f.tid}`}
            />
          </label>
        ))}
      </div>

      <button
        onClick={submit}
        disabled={loading}
        className={`w-full rounded-md px-4 py-2 text-sm font-medium text-white ${
          loading ? "bg-gray-300 cursor-not-allowed" : "bg-violet-600 hover:bg-violet-700"
        }`}
        data-testid="nephrology-stone-submit"
      >
        {loading ? "Изчисляване…" : "Изчисли риск (безплатно)"}
      </button>

      {result && (
        <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-3" data-testid="nephrology-stone-result">
          <div className="flex items-baseline justify-between">
            <span className="text-[11px] uppercase tracking-wide text-gray-500">Риск</span>
            <span className="text-2xl font-bold" style={{ color: result.color }}>
              {result.risk_score}/100
            </span>
          </div>
          <div
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium"
            style={{ background: `${result.color}20`, color: result.color }}
          >
            {result.risk_label_bg}
          </div>
          {result.factors?.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold text-gray-700 mb-1">Рискови фактори:</div>
              <ul className="space-y-1">
                {result.factors.map((f) => (
                  <li key={f.name} className="text-xs text-gray-700 flex items-center gap-1.5">
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        background:
                          f.severity === "high" ? "#EF4444" : f.severity === "moderate" ? "#F59E0B" : "#84CC16",
                      }}
                    />
                    {f.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <div className="text-[11px] font-semibold text-gray-700 mb-1">Препоръки:</div>
            <ul className="space-y-0.5 text-xs text-gray-600">
              {result.recommendations.slice(0, 5).map((r) => (
                <li key={r}>· {r}</li>
              ))}
            </ul>
          </div>
          <div className="text-[10px] text-gray-400 italic leading-relaxed pt-1 border-t border-gray-100">
            {result.reference}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Daily PRAL from meal log ────────────────────────────────────────────
function DailyPralCard({ API }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/nephrology/daily-pral`);
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="text-sm text-gray-400 py-4 text-center">Зареждане…</div>;
  }

  if (!data || data.label === "no_data") {
    return (
      <div className="text-center py-6" data-testid="nephrology-pral-empty">
        <Droplet size={32} className="mx-auto text-gray-300 mb-2" />
        <div className="text-sm text-gray-500">
          Добавете храни в дневника си, за да видите дневното киселинно-алкално натоварване (PRAL).
        </div>
      </div>
    );
  }

  const contribs = Object.entries(data.contributions || {});
  return (
    <div className="space-y-3" data-testid="nephrology-pral-card">
      <div className="rounded-lg border p-3" style={{ borderColor: `${data.color}40`, background: `${data.color}10` }}>
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-600">Общ PRAL днес</div>
            <div className="text-3xl font-bold" style={{ color: data.color }}>
              {data.total_pral > 0 ? "+" : ""}{data.total_pral}
            </div>
            <div className="text-[10px] text-gray-500">{data.unit}</div>
          </div>
          <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium"
            style={{ background: data.color, color: "#fff" }}
          >
            {data.label_bg}
          </span>
        </div>
      </div>

      <div>
        <div className="text-[11px] font-semibold text-gray-700 mb-1.5">Принос по минерал:</div>
        <ul className="space-y-1.5">
          {contribs.map(([key, val]) => {
            const labels = {
              protein: "Протеин", phosphorus: "Фосфор",
              potassium: "Калий", magnesium: "Магнезий", calcium: "Калций",
            };
            const isPositive = val > 0;
            return (
              <li key={key} className="flex items-center justify-between text-xs" data-testid={`pral-contrib-${key}`}>
                <span className="text-gray-700">{labels[key] || key}</span>
                <span className={`font-semibold ${isPositive ? "text-amber-700" : "text-emerald-700"}`}>
                  {isPositive ? "+" : ""}{val} mEq
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="rounded-md bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
        Алкално (&lt; −10) • Леко алкално (−10…0) • Неутр.-киселинно (0…+10) • Силно киселинно (&gt; +10)
      </div>

      <div className="text-[10px] text-gray-400 italic">{data.methodology}</div>
    </div>
  );
}

// ─── Main panel ──────────────────────────────────────────────────────────
export default function NephrologyPanel() {
  const { API } = useAuth();
  const [tab, setTab] = useState("egfr");

  const content = useMemo(() => {
    if (tab === "egfr") return <EgfrForm API={API} />;
    if (tab === "stages") return <CkdStagesTable />;
    if (tab === "stone") return <KidneyStoneForm API={API} />;
    if (tab === "pral") return <DailyPralCard API={API} />;
    return null;
  }, [tab, API]);

  return (
    <CollapsiblePanel
      title="Нефрология"
      titleEn="Nephrology"
      subtitle="CKD-EPI 2021 · Бъбречни камъни · PRAL"
      icon={Activity}
      iconColor="#0EA5E9"
      credits={null}
      storageKey="nephrology-panel"
      testId="nephrology-panel"
      defaultOpen={false}
    >
      <div className="space-y-3">
        <ComplianceDisclaimer context="nephrology" />

        {/* Tab selector */}
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1" role="tablist">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                role="tab"
                aria-selected={active}
                data-testid={`nephrology-tab-${t.id}`}
                className={`flex-1 flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                  active ? "bg-white text-sky-700 shadow-sm" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <Icon size={12} />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            );
          })}
        </div>

        {content}
      </div>
    </CollapsiblePanel>
  );
}
